import React, {useState, useEffect, useContext} from "react";
import axios from "axios";
import withAuth from "../../auth";
import Pagination from "../Pagination";
import moment from 'moment';
import { API_URL } from '../../constants';
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {AuthContext} from "../AuthContext";
import echo from "../../echo";
import PickupCancellationFeeBadge from "./PickupCancellationFeeBadge";
import WhitelabelBadge from "../WhitelabelBadge";
import WhitelabelFilter from "../WhitelabelFilter";
import {
    formatPickupDropoffDisplay,
    formatPickupOriginDisplay,
} from '../../utils/locationDisplay';

const parsePickupListQuery = () => {
    const sp = new URLSearchParams(window.location.search);

    const page = parseInt(sp.get('page') || '1', 10);
    const sortBy = sp.get('sort_by') || 'id';
    const sortOrder = sp.get('sort_order') || 'desc';

    const filters = {};
    ['id', 'whitelabel_id', 'user_id', 'driver_id', 'truck_type_id', 'delivery_status_id'].forEach((k) => {
        const v = sp.get(k);
        if (v) filters[k] = v;
    });

    return {
        page: Number.isNaN(page) ? 1 : page,
        sortBy,
        sortOrder,
        filters,
    };
};

export const PickupList = () => {
    const initial = parsePickupListQuery(window.location.search);

    const [pickups, setPickups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(initial.page);
    const [totalPages, setTotalPages] = useState(1);
    const [, setSearchParams] = useSearchParams();
    const [sortBy, setSortBy] = useState(initial.sortBy);
    const [sortOrder, setSortOrder] = useState(initial.sortOrder);
    const [filters, setFilters] = useState(initial.filters);
    const [deliveryStatuses, setDeliveryStatuses] = useState([]);
    const [selectedPickupIds, setSelectedPickupIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [bulkDeleteError, setBulkDeleteError] = useState('');
    const [bulkDeleteSuccess, setBulkDeleteSuccess] = useState('');
    const [filterInputs, setFilterInputs] = useState({
        id: '',
        whitelabel_id: '',
        user_id: '',
        driver_id: '',
        truck_type_id: '',
        delivery_status_id: '',
        ...initial.filters, // prefill inputs from URL
    });
    const navigate = useNavigate();
    const {token, admin, setToken, hasPermission} = useContext(AuthContext);

    useEffect(() => {
        if (!admin?.id) return;

        const channel = echo.channel('np-admin-pickups');

        channel.listen('.App\\Events\\Admin\\PickupCreated', (event) => {
            if (event.message_type === 'pickup_created' && event.pickup) {
                console.log('event.pickup', event.pickup);
                setPickups((prev) => [event.pickup, ...prev]);
            }
        });

        return () => {
            echo.leave('np-admin-pickups');
        };
    }, [admin]);

    useEffect(() => {
        if (!pickups.length) return;

        const channels = pickups.map((p) => p.channel_id).filter(Boolean);

        channels.forEach((channelId) => {
            const c = echo.channel(channelId);

            c.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
                const updatedPickup = event.pickup;
                if (!updatedPickup) return;

                setPickups((prevPickups) =>
                    prevPickups.map((p) =>
                        p.id === updatedPickup.id
                            ? { ...p, ...updatedPickup }
                            : p
                    )
                );
            });
        });

        return () => {
            channels.forEach((channelId) => {
                echo.leave(channelId);
            });
        };
    }, [pickups]);

    useEffect(() => {
        if (!token) return;
        fetchPickups();
    }, [token, currentPage, sortBy, sortOrder, filters]);

    useEffect(() => {
        const eligibleIds = new Set(
            pickups
                .filter((pickup) => pickup.can_delete === true)
                .map((pickup) => String(pickup.id))
        );

        setSelectedPickupIds((previousIds) => {
            const eligibleSelections = previousIds.filter((id) => eligibleIds.has(String(id)));

            return eligibleSelections.length === previousIds.length
                ? previousIds
                : eligibleSelections;
        });
    }, [pickups]);

    useEffect(() => {
        if (!token) return;

        const fetchDeliveryStatuses = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/admin/delivery-statuses`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                });
                setDeliveryStatuses(response.data.data || []);
            } catch (error) {
                console.error("There was an error fetching delivery statuses!", error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            }
        };

        fetchDeliveryStatuses();
    }, [token, navigate, setToken]);

    const fetchPickups = async (newFilters = null, newPage = null) => {
        setLoading(true);
        setSelectedPickupIds([]);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups`,
                {
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: newPage ?? currentPage,
                    ...(newFilters || filters),
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setPickups(response.data.data);
            setTotalPages(response.data.last_page);
            return response.data;
        } catch (error) {
            console.error("There was an error fetching the drivers!", error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleSortChange = (column) => {
        let nextSortBy = sortBy;
        let nextSortOrder = sortOrder;

        if (sortBy === column) {
            nextSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            nextSortBy = column;
            nextSortOrder = 'asc';
        }

        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        setCurrentPage(1);

        setSearchParams(
            buildSearchParams({
                page: 1,
                sortBy: nextSortBy,
                sortOrder: nextSortOrder,
                filters,
            }),
            { replace: false }
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);

        setSearchParams(
            buildSearchParams({
                page,
                sortBy,
                sortOrder,
                filters,
            }),
            { replace: false }
        );
    };

    const handleFilterChange = (e, column) => {
        setFilterInputs((prev) => ({ ...prev, [column]: e.target.value }));
    };

    const handleSearchSubmit = () => {
        const nonEmptyFilters = Object.keys(filterInputs).reduce((acc, key) => {
            const v = filterInputs[key];
            if (v !== undefined && v !== null && String(v).trim() !== '') acc[key] = v;
            return acc;
        }, {});

        setCurrentPage(1);
        setFilters(nonEmptyFilters);

        setSearchParams(
            buildSearchParams({
                page: 1,
                sortBy,
                sortOrder,
                filters: nonEmptyFilters,
            }),
            { replace: false } // push history so back works nicely
        );
    };

    const handleDropdownChange = (e, column) => {
        const nextInputs = { ...filterInputs, [column]: e.target.value };
        setFilterInputs(nextInputs);

        const nonEmptyFilters = Object.keys(nextInputs).reduce((acc, key) => {
            const v = nextInputs[key];
            if (v !== undefined && v !== null && String(v).trim() !== '') acc[key] = v;
            return acc;
        }, {});

        setCurrentPage(1);
        setFilters(nonEmptyFilters);

        setSearchParams(
            buildSearchParams({
                page: 1,
                sortBy,
                sortOrder,
                filters: nonEmptyFilters,
            }),
            { replace: false }
        );
    };

    const buildSearchParams = (next) => {
        const params = new URLSearchParams();

        // sort/page
        if (next.page) params.set('page', String(next.page));
        if (next.sortBy) params.set('sort_by', next.sortBy);
        if (next.sortOrder) params.set('sort_order', next.sortOrder);

        // filters
        Object.entries(next.filters || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).trim() !== '') {
                params.set(k, String(v));
            }
        });

        return params;
    };

    const selectablePickupIds = pickups
        .filter((pickup) => pickup.can_delete === true)
        .map((pickup) => pickup.id);
    const selectedPickupIdSet = new Set(selectedPickupIds.map(String));
    const allSelectablePickupsSelected = selectablePickupIds.length > 0
        && selectablePickupIds.every((id) => selectedPickupIdSet.has(String(id)));
    const canBulkDelete = hasPermission('pickup-update');

    const togglePickupSelection = (pickupId) => {
        setSelectedPickupIds((previousIds) => {
            const pickupIdKey = String(pickupId);
            const isSelected = previousIds.some((id) => String(id) === pickupIdKey);

            return isSelected
                ? previousIds.filter((id) => String(id) !== pickupIdKey)
                : [...previousIds, pickupId];
        });
    };

    const toggleAllSelectablePickups = () => {
        setSelectedPickupIds(allSelectablePickupsSelected ? [] : selectablePickupIds);
    };

    const handleBulkDelete = async () => {
        const eligibleIdSet = new Set(selectablePickupIds.map(String));
        const pickupIds = selectedPickupIds.filter((id) => eligibleIdSet.has(String(id)));

        if (pickupIds.length === 0 || isBulkDeleting) return;

        const deliveryLabel = pickupIds.length === 1 ? 'delivery' : 'deliveries';
        if (!window.confirm(
            `Permanently delete ${pickupIds.length} selected ${deliveryLabel}? This action cannot be undone.`
        )) return;

        setIsBulkDeleting(true);
        setBulkDeleteError('');
        setBulkDeleteSuccess('');

        try {
            const response = await axios.delete(`${API_URL}/api/admin/pickups`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                data: {
                    pickup_ids: pickupIds,
                },
            });

            setBulkDeleteSuccess(
                response.data?.message
                || `${pickupIds.length} ${deliveryLabel} deleted successfully.`
            );

            const refreshedPage = await fetchPickups();
            if (refreshedPage && refreshedPage.data.length === 0 && currentPage > 1) {
                handlePageChange(currentPage - 1);
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
                return;
            }

            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors)
                    .map((key) => errors[key].join(' '))
                    .join(' ');
                setBulkDeleteError(errorMessages);
            } else if (error?.response?.data?.message) {
                setBulkDeleteError(error.response.data.message);
            } else {
                setBulkDeleteError('Unable to delete the selected deliveries. Please try again.');
            }

            if ([409, 422].includes(error?.response?.status)) {
                const refreshedPage = await fetchPickups();
                if (refreshedPage && refreshedPage.data.length === 0 && currentPage > 1) {
                    handlePageChange(currentPage - 1);
                }
            }
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const statusColors = {
        '0': 'transparent',
        '1': '#FFFAE5',  // Very Light Gold
        '2': '#FFCBB2',  // Very Light OrangeRed
        '3': '#CFF3C8',  // Very Light Green
        '4': '#C7D6E9',  // Very Light SteelBlue
        '5': '#D1F2CF',  // Very Light LimeGreen
        '6': '#FFA3B5'   // Very Light Crimson
    };

    const truncateWithEllipsis = (text, maxLength = 30) => {
        if (text.length > maxLength) {
            return (
                <span title={text}>
                    {text.substring(0, maxLength)}...
                </span>
            );
        }
        return text;
    };

    const renderShopifyBadge = (pickup) => {
        if (!pickup.shopify_order_id) return null;

        return (
            <div className="mb-1">
                <span
                    className="badge badge-primary"
                    title={`Shopify order ID: ${pickup.shopify_order_id}`}
                >
                    Shopify #{pickup.shopify_order_number || pickup.shopify_order_id}
                </span>
            </div>
        );
    };

    const renderAdminNotes = (pickup) => {
        const notes = String(pickup.admin_notes || '').trim();

        if (!notes) {
            return null;
        }

        const preview = notes.replace(/\s+/g, ' ');

        return (
            <div className="mt-1" title={notes}>
                <span className="badge badge-warning mb-1">
                    <i className="fa fa-sticky-note mr-1" aria-hidden="true"></i>
                    {truncateWithEllipsis(preview, 42)}
                </span>
            </div>
        );
    };

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Delivery List</h2>
            {bulkDeleteSuccess && <div className="alert alert-success">{bulkDeleteSuccess}</div>}
            {bulkDeleteError && <div className="alert alert-danger">{bulkDeleteError}</div>}
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <div>
                    {canBulkDelete && (
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                            <small className="text-muted mr-3">
                                Only canceled deliveries with no fees, invoices, or unresolved payment state can be deleted.
                            </small>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={handleBulkDelete}
                                disabled={selectedPickupIds.length === 0 || isBulkDeleting}
                            >
                                {isBulkDeleting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-1"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Deleting...
                                    </>
                                ) : `Delete selected (${selectedPickupIds.length})`}
                            </button>
                        </div>
                    )}
                    <div className="table-responsive">
                    <table className="table table-sm table-striped text-center text-nowrap">
                        <thead>
                        <tr>
                            {canBulkDelete && (
                                <th>
                                    {selectablePickupIds.length > 0 && (
                                        <input
                                            type="checkbox"
                                            aria-label="Select all deletable deliveries on this page"
                                            checked={allSelectablePickupsSelected}
                                            onChange={toggleAllSelectablePickups}
                                            disabled={isBulkDeleting}
                                        />
                                    )}
                                </th>
                            )}
                            <th onClick={() => handleSortChange('id')}>
                                ID <i
                                className={`fa fa-sort${sortBy === 'id' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th>Whitelabel</th>
                            <th onClick={() => handleSortChange('delivery_status_id')}>
                                Status <i
                                className={`fa fa-sort${sortBy === 'delivery_status_id' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th onClick={() => handleSortChange('user_id')}>
                                Customer <i
                                className={`fa fa-sort${sortBy === 'user_id' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th onClick={() => handleSortChange('driver_id')}>
                                Driver <i
                                className={`fa fa-sort${sortBy === 'driver_id' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th onClick={() => handleSortChange('truck_type_id')}>
                                Truck Type <i
                                className={`fa fa-sort${sortBy === 'truck_type_id' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th onClick={() => handleSortChange('cost')}>
                                Cost / Dist. <i
                                className={`fa fa-sort${sortBy === 'cost' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                            <th>
                                Locations
                            </th>
                            <th onClick={() => handleSortChange('created_at')}>
                                Created At <i
                                className={`fa fa-sort${sortBy === 'created_at' ? (sortOrder === 'asc' ? '-asc' : '-desc') : ''}`}/>
                            </th>
                        </tr>
                        <tr>
                            {canBulkDelete && <td></td>}
                            {/* ID */}
                            <td>
                                <div className="input-group d-flex justify-content-center">
                                    <input
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filterInputs.id}
                                        placeholder='ID'
                                        onChange={(e) => handleFilterChange(e, 'id')}
                                        onKeyPress={e => {
                                            if (e.key === 'Enter') handleSearchSubmit(e)
                                        }}
                                        style={{maxWidth: '80px'}}
                                    />
                                    <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                        <i className="fa fa-search" />
                                    </button>
                                </div>
                            </td>
                            {/* Whitelabel */}
                            <td>
                                <WhitelabelFilter
                                    value={filterInputs.whitelabel_id}
                                    onChange={(e) => handleDropdownChange(e, 'whitelabel_id')}
                                />
                            </td>
                            {/* Delivery Status */}
                            <td>
                                <div className="input-group d-flex justify-content-center">
                                    <select
                                        className="form-control form-control-sm text-center"
                                        value={filterInputs.delivery_status_id}
                                        onChange={(e) => handleDropdownChange(e, 'delivery_status_id')}
                                        style={{maxWidth: "100px"}}
                                    >
                                        <option value="">All</option>
                                        {deliveryStatuses.map((status) => (
                                            <option key={status.id} value={status.id}>
                                                {status.status}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </td>
                            {/* Customer */}
                            <td>
                                <div className="input-group">
                                    <input
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filterInputs.user_id}
                                        placeholder='Customer ID'
                                        onChange={(e) => handleFilterChange(e, 'user_id')}
                                        onKeyPress={e => {
                                            if (e.key === 'Enter') handleSearchSubmit(e)
                                        }}
                                    />
                                    <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                        <i className="fa fa-search" />
                                    </button>
                                </div>
                            </td>
                            {/* Driver */}
                            <td>
                                <div className="input-group">
                                    <input
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filterInputs.driver_id}
                                        placeholder='Driver ID'
                                        onChange={(e) => handleFilterChange(e, 'driver_id')}
                                        onKeyPress={e => {
                                            if (e.key === 'Enter') handleSearchSubmit(e)
                                        }}
                                    />
                                    <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                        <i className="fa fa-search" />
                                    </button>
                                </div>
                            </td>
                            {/* Truck Type */}
                            <td>
                                <div className="input-group">
                                    <select
                                        className="form-control form-control-sm text-center"
                                        value={filterInputs.truck_type_id}
                                        onChange={(e) => handleDropdownChange(e, 'truck_type_id')}
                                    >
                                        <option value="">All</option>
                                        <option value="1">Delivery</option>
                                        <option value="2">Flatbed</option>
                                        <option value="3">Cube Van</option>
                                        <option value="4">Cargo Van</option>
                                        <option value="11">Moffett</option>
                                    </select>
                                </div>
                            </td>
                            {/* Cost / Distance */}
                            <td></td>
                            {/* Locations */}
                            <td></td>
                            {/* Created At */}
                            <td></td>
                        </tr>
                        </thead>
                        <tbody className="text-wrap">
                        {pickups.map((pickup) => (
                            <tr key={pickup.id}>
                                {canBulkDelete && (
                                    <td>
                                        {pickup.can_delete === true ? (
                                            <input
                                                type="checkbox"
                                                aria-label={`Select delivery ${pickup.id}`}
                                                checked={selectedPickupIdSet.has(String(pickup.id))}
                                                onChange={() => togglePickupSelection(pickup.id)}
                                                disabled={isBulkDeleting}
                                            />
                                        ) : (
                                            <span className="text-muted" aria-hidden="true">&mdash;</span>
                                        )}
                                    </td>
                                )}
                                <td style={{backgroundColor: !pickup.delivery_status?.id ? 'initial' : statusColors[pickup.delivery_status?.id] || 'initial'}}>
                                    <Link to={`/pickups/${pickup.id}`}>
                                        {pickup.id}
                                    </Link>
                                    {pickup.user && hasPermission('user-update') && (
                                        <div>
                                            <Link to={`/pickups/create/${pickup.user.id}?duplicate=${pickup.id}`}
                                                  className='btn btn-sm btn-outline-primary'>
                                                Duplicate
                                            </Link>
                                        </div>
                                    )}
                                    {renderAdminNotes(pickup)}
                                </td>
                                <td>
                                    <WhitelabelBadge whitelabelId={pickup.whitelabel_id} />
                                </td>
                                <td>
                                    {pickup.delivery_status?.id !== 5 && pickup.delivery_status?.id !== 6 && pickup.waiting_for_driver_at &&
                                        <div><span className="badge badge-success">Live Request</span></div>}
                                    {pickup.delivery_status?.status ? (
                                        <div className="badge badge-light">{pickup.delivery_status.status}</div>
                                    ) : (
                                        pickup.delivery_status?.id === 0 && !pickup.waiting_for_driver_at && pickup.claim?.status === 'unclaimed' && (
                                            <div className="badge badge-light">Created</div>
                                        )
                                    )}
                                    {pickup.canceled_with_fee === true && (
                                        <div className="mt-1">
                                            <PickupCancellationFeeBadge canceledWithFee />
                                        </div>
                                    )}

                                    {renderShopifyBadge(pickup)}
                                </td>
                                <td>
                                    {pickup.user &&
                                        <Link to={`/users/${pickup.user.id}`}>
                                            {pickup.user.id}<br/>{pickup.user.full_name}
                                        </Link>
                                    }
                                </td>
                                <td>
                                    {pickup.driver &&
                                        <Link to={`/drivers/${pickup.driver.id}`}>
                                            {pickup.driver.id}<br/>{pickup.driver.name}
                                        </Link>
                                    }
                                </td>
                                <td>{pickup.truck_type?.title}</td>
                                <td>${pickup.cost}<br/>{pickup.distance} km</td>
                                <td className="text-nowrap">
                                    {truncateWithEllipsis(formatPickupOriginDisplay(pickup))}<br/>
                                    {truncateWithEllipsis(formatPickupDropoffDisplay(pickup))}
                                </td>
                                <td>
                                    {moment(pickup.created_at).format('YYYY-MM-DD HH:mm')}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {totalPages > 1 && currentPage <= totalPages &&
                        <Pagination currentPage={currentPage} totalPages={totalPages}
                                    handlePageChange={handlePageChange}/>
                    }
                    </div>
                </div>
            )}
        </div>
    );
};

export default withAuth(PickupList);
